// React
import { memo, useCallback, useState } from "react";

import InvoiceFieldHighlighter from "./InvoiceFieldHighlighter";
import { IMAGE_EXTENSIONS } from "./consts";
import { InvoiceDocumentViewerProps } from "./types";

/** Koliko se prikaz uvećava po koraku i dokle ide. */
const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

/**
 * Prikaz računa uz obrazac — stoji u lijevom stupcu modala, pa mu visinu daje sam stupac. PDF
 * preuzima ugrađeni preglednik preglednika, a slike se crtaju ovdje jer se preko njih postavljaju
 * oznake polja; te oznake dolaze s koordinatama koje daje OCR.
 */
const InvoiceDocumentViewer: React.FC<InvoiceDocumentViewerProps> = (props) => {
  const {
    url,
    fileExtension,
    fieldLocations = {},
    activeField,
    rotation = 0,
    fieldValues,
  } = props;

  const [zoom, setZoom] = useState<number>(1);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
  }, []);

  if (!url) return null;

  const isImage = IMAGE_EXTENSIONS.includes((fileExtension ?? "").toLowerCase());

  // PDF prikazuje ugrađeni preglednik preglednika — ima stranice, zum i traženje teksta.
  if (!isImage) {
    return <iframe src={url} title="Račun" className="w-full h-full border-0 bg-slate-900" />;
  }

  return (
    <div className="h-full w-full bg-slate-900 flex flex-col">
      {/* Traka s uvećanjem — isti raspored kao u pregledniku PDF-a. */}
      <div className="h-16 shrink-0 flex items-center justify-center gap-2 text-white">
        <button
          type="button"
          className="px-3 py-1 rounded hover:bg-white/10 disabled:opacity-40"
          onClick={handleZoomOut}
          disabled={zoom <= MIN_ZOOM}
        >
          −
        </button>

        <button
          type="button"
          className="min-w-[64px] text-sm tabular-nums hover:text-slate-300"
          onClick={handleZoomReset}
        >
          {Math.round(zoom * 100)} %
        </button>

        <button
          type="button"
          className="px-3 py-1 rounded hover:bg-white/10 disabled:opacity-40"
          onClick={handleZoomIn}
          disabled={zoom >= MAX_ZOOM}
        >
          +
        </button>
      </div>

      <div className="flex-1 overflow-auto px-6 pb-8 flex justify-center">
        {/* Okviri polja idu preko slike, pa oboje moraju biti u istom omotu iste veličine. */}
        <div
          className="relative h-fit"
          style={{
            width: `${zoom * 100}%`,
            transform: rotation ? `rotate(${rotation}deg)` : undefined,
          }}
        >
          <img src={url} alt="Račun" className="w-full rounded-md shadow-lg" draggable={false} />

          {Object.keys(fieldLocations).length > 0 && (
            <InvoiceFieldHighlighter
              fieldLocations={fieldLocations}
              activeField={activeField}
              fieldValues={fieldValues}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(InvoiceDocumentViewer);
